import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { canBeBillable, dayKey, dayStartOf, earnedByCurrency, formatMoney, shiftDays, sortCurrencies, type EditScope } from "@otrack/domain";
import { Button, Icon, IconButton, Menu, Modal, Segmented, type SegmentOption } from "@otrack/ui";
import { projectRefs } from "../commands";
import { useApp, useDataset, useFactory } from "../context";
import { navigate, type CalendarView } from "../router";
import { formatDuration, MINUTE, startOfDay } from "../time";
import { clipsOf, itemLabel, useCalendarCommands, type AskScope, type Clip, type Draft } from "./actions";
import { ContextMenu, type ContextItem } from "./context-menu";
import { EventEditor, type EventDraft } from "./event-editor";
import { useGoogleState, useGoogleView } from "./google";
import { collectItems, colorOf, isEditablePlan, isEntry, trackedIn, type CalItem, type PlanItem } from "./model";
import { MonthGrid } from "./month-grid";
import { CalendarPopover, type PopoverTarget } from "./popover";
import { ScopeDialog } from "./scope-dialog";
import { MAX_HOUR, MIN_HOUR, TimeGrid, type MenuTarget, type Slot } from "./time-grid";

// Versioned so a zoom remembered from before the tallest-hours default starts over once.
const HOUR_KEY = "otrack:calendar:hour:v2";

function useNarrow(): boolean {
  const query = typeof window === "undefined" || !window.matchMedia ? null : window.matchMedia("(max-width: 760px)");
  const [narrow, setNarrow] = useState(query?.matches ?? false);
  useEffect(() => {
    if (!query) return;
    const listener = (event: MediaQueryListEvent) => setNarrow(event.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, [query]);
  return narrow;
}

function storedHour(): number {
  try {
    const value = Number(localStorage.getItem(HOUR_KEY));
    if (value >= MIN_HOUR && value <= MAX_HOUR) return value;
  } catch { /* Storage may be off; the default is fine. */ }
  // Tallest hours by default, so short entries stay readable; zooming out is a choice.
  return MAX_HOUR;
}

const monday = (day: number) => shiftDays(day, -((new Date(day).getDay() + 6) % 7));

/** The days a view shows around `anchor`. Month returns whole weeks. */
function viewDays(view: CalendarView, anchor: number, hideWeekends: boolean): number[] {
  if (view === "day") return [anchor];
  if (view === "3day") return [anchor, shiftDays(anchor, 1), shiftDays(anchor, 2)];
  if (view === "week") {
    const days = Array.from({ length: 7 }, (_, index) => shiftDays(monday(anchor), index));
    return hideWeekends ? days.filter((day) => ![0, 6].includes(new Date(day).getDay())) : days;
  }
  const first = new Date(new Date(anchor).getFullYear(), new Date(anchor).getMonth(), 1).getTime();
  const last = new Date(new Date(anchor).getFullYear(), new Date(anchor).getMonth() + 1, 0).getTime();
  const days: number[] = [];
  for (let day = monday(first); day <= last || new Date(day).getDay() !== 1; day = shiftDays(day, 1)) days.push(day);
  return days;
}

function step(view: CalendarView, anchor: number, direction: 1 | -1): number {
  if (view === "day") return shiftDays(anchor, direction);
  if (view === "3day") return shiftDays(anchor, 3 * direction);
  if (view === "week") return shiftDays(anchor, 7 * direction);
  const date = new Date(anchor);
  return new Date(date.getFullYear(), date.getMonth() + direction, 1).getTime();
}

const monthTitle = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const shortDate = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const fullDay = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

function titleOf(view: CalendarView, days: number[], anchor: number): { title: string; subtitle: string } {
  if (view === "month") return { title: monthTitle.format(anchor), subtitle: "" };
  if (view === "day") return { title: monthTitle.format(anchor), subtitle: fullDay.format(anchor) };
  const first = days[0]!, last = days.at(-1)!;
  return { title: monthTitle.format(first), subtitle: `${shortDate.format(first)} - ${new Date(first).getMonth() === new Date(last).getMonth() ? new Date(last).getDate() : shortDate.format(last)}` };
}

/**
 * The element drawn for a key. Items that cross midnight are drawn once per day, so prefer the
 * one under `point`, then the one most visible in the window.
 */
function blockFor(key: string, point: { x: number; y: number } | null): HTMLElement | null {
  const candidates = [...document.querySelectorAll<HTMLElement>(`[data-key="${CSS.escape(key)}"]`)];
  if (candidates.length < 2) return candidates[0] ?? null;
  const contains = (rect: DOMRect) => point !== null && point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
  const visible = (rect: DOMRect) => Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)) * Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
  return candidates.find((element) => contains(element.getBoundingClientRect()))
    ?? candidates.sort((a, b) => visible(b.getBoundingClientRect()) - visible(a.getBoundingClientRect()))[0]!;
}

const isTyping = (target: EventTarget | null) => Boolean((target as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable='true'], [role='listbox'], [role='menu']"));

const SHORTCUTS: Array<[string, string]> = [
  ["T", "Go to today"], ["J  K", "Next / previous period"], ["D  W  M", "Day, week, month"], ["N", "New block at the pointer"],
  ["Enter", "Edit the selected block"], ["Shift Enter", "Open the full editor"], ["Delete", "Delete the selection"],
  ["Arrows", "Move between blocks"], ["Alt Arrows", "Nudge 5 minutes or a day"], ["Ctrl C  Ctrl V", "Copy, paste at the pointer"],
  ["Ctrl A", "Select everything shown"], ["+  -", "Zoom the hours"], ["Esc", "Close or clear the selection"],
  ["Shift drag", "Select several blocks"], ["Alt drag", "Copy while dragging"], ["Ctrl scroll", "Zoom around the pointer"]
];

export function CalendarPage({ view: requestedView, date }: { view: CalendarView | null; date: string | null }) {
  const app = useApp();
  const { now, notify, openEntry, google } = app;
  const dataset = useDataset();
  const factory = useFactory();
  const narrow = useNarrow();
  const settings = dataset.settings;
  // Phones show three days where a desktop shows the week.
  const view: CalendarView = requestedView === null || requestedView === "week" ? (narrow ? "3day" : "week") : requestedView;
  const anchor = date ? dayStartOf(date) : startOfDay(now);
  const days = useMemo(() => viewDays(view, anchor, settings.hideWeekends && view === "week"), [view, anchor, settings.hideWeekends]);
  const from = days[0]!, to = shiftDays(days.at(-1)!, 1);

  const [hour, setHourState] = useState(storedHour);
  const setHour = useCallback((next: number) => {
    const clamped = Math.round(Math.min(MAX_HOUR, Math.max(MIN_HOUR, next)));
    setHourState(clamped);
    try { localStorage.setItem(HOUR_KEY, String(clamped)); } catch { /* Not remembered then. */ }
  }, []);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [popover, setPopover] = useState<PopoverTarget | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; target: MenuTarget } | null>(null);
  const [editor, setEditor] = useState<{ plan: PlanItem | null; draft: EventDraft | null } | null>(null);
  const [scope, setScope] = useState<{ verb: "change" | "delete"; allowThis: boolean } | null>(null);
  const [help, setHelp] = useState(false);
  const scopeResolver = useRef<((scope: EditScope | null) => void) | null>(null);
  const clipboard = useRef<Clip[]>([]);
  const hoverRef = useRef<Slot | null>(null);
  // Where the last press landed: a block drawn on two days anchors to the half that was pressed,
  // and a block taller than the screen anchors at the spot that was pressed.
  const lastPress = useRef<{ x: number; y: number; key: string | null; offset: number } | null>(null);

  const askScope = useCallback<AskScope>((options) => new Promise((resolve) => {
    scopeResolver.current = resolve;
    setScope(options);
  }), []);
  const chooseScope = (value: EditScope | null) => {
    setScope(null);
    scopeResolver.current?.(value);
    scopeResolver.current = null;
  };
  const commands = useCalendarCommands(askScope);

  /* ------------------------------------------------------------ Google Calendar */

  const googleState = useGoogleState(google);
  const googleView = useGoogleView(google, settings);
  // Opening the Calendar fetches what changed in Google; the app keeps it fresh after that.
  useEffect(() => { void google?.sync(); }, [google]);

  /* ------------------------------------------------------------ items */

  // The running Timer grows by the minute; recomputing more often only costs time.
  const minute = Math.floor(now / 30_000);
  const { entries, plans } = useMemo(() => collectItems(dataset, googleView, from, to, now), [dataset, googleView, from, to, minute]);
  const all = useMemo<CalItem[]>(() => [...entries, ...plans], [entries, plans]);
  const byKey = useMemo(() => new Map(all.map((item) => [item.key, item])), [all]);
  const selected = useMemo(() => [...selection].map((key) => byKey.get(key)).filter((item): item is CalItem => Boolean(item)), [selection, byKey]);

  useEffect(() => { setSelection(new Set()); setDraft(null); setPopover(null); }, [from, to]);
  useEffect(() => {
    if (popover?.mode === "item" && !byKey.has(popover.key)) setPopover(null);
  }, [popover, byKey]);

  /* ------------------------------------------------------------ navigation */

  const go = useCallback((nextView: CalendarView, day: number) => navigate({ page: "calendar", view: nextView, date: dayKey(day) }), []);
  const today = () => go(view, startOfDay(Date.now()));
  const move = (direction: 1 | -1) => go(view, step(view, anchor, direction));

  /* ------------------------------------------------------------ opening things */

  const closeAll = useCallback(() => {
    setPopover(null);
    setDraft(null);
    setMenu(null);
  }, []);

  const openDraft = useCallback((next: Draft) => {
    setSelection(new Set());
    setMenu(null);
    if (view === "month") {
      setDraft(null);
      setEditor({ plan: null, draft: { ...next, title: "", projectId: null, taskId: null } });
      return;
    }
    setDraft(next);
    setPopover({ mode: "draft", draft: next });
  }, [view]);

  const openItem = useCallback((key: string) => {
    setDraft(null);
    setMenu(null);
    setPopover({ mode: "item", key });
  }, []);

  const editItem = useCallback((item: CalItem) => {
    closeAll();
    if (isEntry(item)) return openEntry(item.entry.id);
    if (isEditablePlan(item)) return setEditor({ plan: item, draft: null });
    setSelection(new Set([item.key]));
    setPopover({ mode: "item", key: item.key });
  }, [closeAll, openEntry]);

  const onBackground = useCallback(() => {
    const open = popover !== null || draft !== null || selection.size > 0 || menu !== null;
    if (open) { closeAll(); setSelection(new Set()); }
    return open;
  }, [popover, draft, selection.size, menu, closeAll]);

  const anchorOf = useCallback(() => blockFor(popover?.mode === "item" ? popover.key : "draft", lastPress.current), [popover]);

  /* ------------------------------------------------------------ context menu */

  const slotTime = (slot: Slot | null): number | null => {
    if (!slot) return null;
    if (slot.minute < 0) return slot.day + 9 * 60 * MINUTE;
    return new Date(slot.day).setHours(0, Math.floor(slot.minute / 15) * 15, 0, 0);
  };

  const menuItems = (target: MenuTarget): ContextItem[] => {
    const projects = projectRefs(dataset);
    const projectChildren = (items: CalItem[]): ContextItem[] => [
      { label: "No project", swatch: colorOf(dataset, null), onSelect: () => void commands.setProject(items, null) },
      ...projects.map((ref) => ({ label: ref.clientName ? `${ref.name}, ${ref.clientName}` : ref.name, swatch: colorOf(dataset, ref.id), onSelect: () => void commands.setProject(items, ref.id) }))
    ];
    if (!target.item) {
      const at = slotTime(target.slot);
      if (at === null) return [];
      const allDay = target.slot!.minute < 0;
      const past = at < now - 5 * MINUTE;
      return [
        { label: "New entry here", icon: "clock", disabled: !past || allDay, onSelect: () => openDraft({ kind: "entry", start: at, end: Math.min(now, at + 30 * MINUTE), allDay: false }) },
        { label: allDay ? "New all-day event" : "New event here", icon: "calendar-plus", hint: "N", onSelect: () => openDraft(allDay ? { kind: "event", start: target.slot!.day, end: shiftDays(target.slot!.day, 1), allDay: true } : { kind: "event", start: at, end: at + 60 * MINUTE, allDay: false }) },
        { label: clipboard.current.length > 1 ? `Paste ${clipboard.current.length} items` : "Paste", icon: "clipboard", hint: "Ctrl V", disabled: !clipboard.current.length, onSelect: () => void commands.paste(clipboard.current, allDay ? target.slot!.day : at) }
      ];
    }
    const item = target.item;
    const group = selection.has(item.key) && selected.length > 1 ? selected : [item];
    if (group.length > 1) {
      const entryGroup = group.filter(isEntry);
      return [
        { label: `Copy ${group.length} items`, icon: "copy", hint: "Ctrl C", onSelect: () => { clipboard.current = clipsOf(group); notify(`Copied ${group.length} items`); } },
        { label: "Change project", icon: "folder", children: projectChildren(group) },
        ...(entryGroup.length ? [{ label: "Toggle billable", icon: "coins" as const, onSelect: () => void commands.toggleBillable(entryGroup) }] : []),
        { label: `Delete ${group.length} items`, icon: "trash", tone: "danger", hint: "Del", onSelect: () => void commands.removeItems(group) }
      ];
    }
    const copy: ContextItem = { label: "Copy", icon: "copy", hint: "Ctrl C", onSelect: () => { clipboard.current = clipsOf([item]); notify(`Copied ${itemLabel(dataset, item)}`); } };
    if (isEntry(item)) {
      const project = item.projectId ? dataset.projects[item.projectId] : undefined;
      return [
        { label: "Edit", icon: "pencil", hint: "Enter", onSelect: () => openItem(item.key) },
        { label: "Open details", icon: "file-text", hint: "Shift Enter", onSelect: () => openEntry(item.entry.id) },
        ...(item.running ? [{ label: "Stop timer", icon: "stop" as const, onSelect: () => void commands.stop() }] : []),
        copy,
        { label: "Duplicate after it", icon: "plus", disabled: item.running, onSelect: () => void commands.paste(clipsOf([item]), item.end) },
        { label: "Change project", icon: "folder", disabled: item.locked, children: projectChildren([item]) },
        { label: item.billable ? "Make non-billable" : "Make billable", icon: "coins", disabled: item.locked || !canBeBillable(project), onSelect: () => void commands.toggleBillable([item]) },
        { label: "Move to Trash", icon: "trash", tone: "danger", hint: "Del", disabled: item.locked, onSelect: () => void commands.removeItems([item]) }
      ];
    }
    const plan = item;
    const editable = isEditablePlan(plan);
    const readOnly = plan.kind === "google" && !editable;
    return [
      { label: editable ? "Edit" : "Show", icon: "pencil", hint: "Enter", onSelect: () => openItem(plan.key) },
      ...(editable ? [{ label: "More options", icon: "settings" as const, hint: "Shift Enter", onSelect: () => setEditor({ plan, draft: null }) }] : []),
      ...(!plan.allDay && plan.start < now ? [{ label: "Log as worked", icon: "check" as const, onSelect: () => (plan.projectId || plan.kind === "event" ? void commands.logPlan(plan) : openItem(plan.key)) }] : []),
      { label: "Start timer", icon: "play", onSelect: () => void commands.startPlan(plan) },
      copy,
      { label: readOnly ? "Copy into my plans" : "Duplicate", icon: "plus", onSelect: () => void commands.shiftItems([plan], 0, true) },
      { label: readOnly ? "Project for this series" : "Change project", icon: "folder", children: projectChildren([plan]) },
      ...(plan.google?.occurrence.event.htmlLink ? [{ label: "Open in Google Calendar", icon: "arrow-up-right" as const, onSelect: () => void window.open(plan.google!.occurrence.event.htmlLink, "_blank", "noopener") }] : []),
      ...(editable ? [{ label: "Delete", icon: "trash" as const, tone: "danger" as const, hint: "Del", onSelect: () => void commands.removeItems([plan]) }] : [])
    ];
  };

  const openMenu = useCallback((x: number, y: number, target: MenuTarget) => {
    setPopover(null);
    setDraft(null);
    setMenu({ x, y, target });
  }, []);

  /* ------------------------------------------------------------ keyboard */

  const focusKey = (key: string) => {
    setSelection(new Set([key]));
    requestAnimationFrame(() => {
      const element = blockFor(key, null);
      element?.focus({ preventScroll: true });
      element?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
  };

  const neighbour = (from: CalItem, key: string): CalItem | null => {
    const timed = all.filter((item) => !("allDay" in item && item.allDay)).sort((a, b) => a.start - b.start);
    const dayOf = (item: CalItem) => days.findIndex((day) => item.start < shiftDays(day, 1) && item.end > day);
    const minuteOf = (item: CalItem) => { const date = new Date(item.start); return date.getHours() * 60 + date.getMinutes(); };
    const fromDay = dayOf(from);
    if (key === "ArrowUp" || key === "ArrowDown") {
      const same = timed.filter((item) => dayOf(item) === fromDay);
      const index = same.findIndex((item) => item.key === from.key);
      return same[index + (key === "ArrowDown" ? 1 : -1)] ?? null;
    }
    const direction = key === "ArrowRight" ? 1 : -1;
    for (let day = fromDay + direction; day >= 0 && day < days.length; day += direction) {
      const there = timed.filter((item) => dayOf(item) === day);
      if (there.length) return there.sort((a, b) => Math.abs(minuteOf(a) - minuteOf(from)) - Math.abs(minuteOf(b) - minuteOf(from)))[0]!;
    }
    return null;
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || isTyping(event.target)) return;
      if (document.querySelector(".ot-modal-backdrop, .ot-palette-backdrop, .ot-calpop, .ot-context")) {
        if (event.key === "Escape" && (popover || menu)) { event.preventDefault(); closeAll(); }
        return;
      }
      const mod = event.ctrlKey || event.metaKey;
      const key = event.key;
      const lower = key.toLowerCase();
      const only = selected.length === 1 ? selected[0]! : null;

      if (mod && lower === "c" && selected.length) { event.preventDefault(); clipboard.current = clipsOf(selected); return notify(selected.length === 1 ? `Copied ${itemLabel(dataset, selected[0]!)}` : `Copied ${selected.length} items`); }
      if (mod && lower === "v") {
        event.preventDefault();
        const slot = hoverRef.current;
        const at = slot ? new Date(slot.day).setHours(0, Math.floor(slot.minute / 15) * 15, 0, 0) : only ? only.end : null;
        if (at === null) return notify("Point at a time to paste there");
        return void commands.paste(clipboard.current, at);
      }
      if (mod && lower === "a") { event.preventDefault(); return setSelection(new Set(all.map((item) => item.key))); }
      if (mod || (event.altKey && !key.startsWith("Arrow"))) return;

      if (key === "Escape") { if (selection.size || draft) { event.preventDefault(); closeAll(); setSelection(new Set()); } return; }
      if ((key === "Delete" || key === "Backspace") && selected.length) { event.preventDefault(); return void commands.removeItems(selected); }
      if (key === "Enter" && only) { event.preventDefault(); return event.shiftKey ? editItem(only) : openItem(only.key); }
      if (key.startsWith("Arrow")) {
        if (event.altKey && selected.length && view !== "month") {
          event.preventDefault();
          const vertical = key === "ArrowUp" || key === "ArrowDown";
          const amount = key === "ArrowUp" || key === "ArrowLeft" ? -1 : 1;
          const movable = selected.filter((item) => !isEntry(item) || (!item.locked && !item.running));
          return void commands.shiftItems(movable, vertical ? amount * 5 * MINUTE : shiftDays(movable[0]?.start ?? now, amount) - (movable[0]?.start ?? now), false, vertical ? 0 : amount);
        }
        if (only && view !== "month") {
          const next = neighbour(only, key);
          event.preventDefault();
          if (next) focusKey(next.key);
          return;
        }
        if (key === "ArrowLeft" || key === "ArrowRight") { event.preventDefault(); return move(key === "ArrowRight" ? 1 : -1); }
        return;
      }
      if (event.altKey) return;
      switch (lower) {
        case "t": event.preventDefault(); return today();
        case "j": event.preventDefault(); return move(1);
        case "k": event.preventDefault(); return move(-1);
        case "d": event.preventDefault(); return go("day", only ? startOfDay(only.start) : anchor);
        case "w": event.preventDefault(); return go(narrow ? "3day" : "week", anchor);
        case "m": event.preventDefault(); return go("month", anchor);
        case "+": case "=": event.preventDefault(); return setHour(hour + 8);
        case "-": case "_": event.preventDefault(); return setHour(hour - 8);
        case "?": event.preventDefault(); return setHelp(true);
        case "n": {
          event.preventDefault();
          const slot = hoverRef.current;
          if (view === "month") return setEditor({ plan: null, draft: { start: anchor + 9 * 60 * MINUTE, end: anchor + 10 * 60 * MINUTE, allDay: false, title: "", projectId: null, taskId: null } });
          const base = slot ? new Date(slot.day).setHours(0, Math.floor(slot.minute / 15) * 15, 0, 0) : Math.ceil(Date.now() / (15 * MINUTE)) * 15 * MINUTE;
          return openDraft({ kind: "event", start: base, end: base + 60 * MINUTE, allDay: false });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ------------------------------------------------------------ totals */

  const tracked = trackedIn(entries, from, to);
  const earned = earnedByCurrency(dataset, { from, to: Math.min(to, now + 1) }, now);
  const currencies = sortCurrencies(Object.keys(earned)).filter((currency) => earned[currency]);
  const { title, subtitle } = titleOf(view, days, anchor);
  const popoverItem = popover?.mode === "item" ? byKey.get(popover.key) ?? null : null;
  const unit = view === "month" ? "month" : view === "day" ? "day" : view === "3day" ? "3 days" : "week";
  const viewOptions: Array<SegmentOption<CalendarView>> = [
    { value: "day", label: "Day" },
    narrow ? { value: "3day", label: "3 days" } : { value: "week", label: "Week" },
    { value: "month", label: "Month" }
  ];
  if (!narrow && view === "3day") viewOptions.splice(1, 0, { value: "3day", label: "3 days" });
  const googleNotice = googleState.phase === "reconnect" ? "reconnect" : googleState.pending.length > 0 && googleState.error ? "waiting" : null;

  return <div className="ot-page ot-cal">
    <header className="ot-cal__bar">
      <div className="ot-cal__titles">
        <h1>{title}</h1>
        {subtitle && <span className="ot-cal__subtitle">{subtitle}</span>}
      </div>
      <div className="ot-cal__nav">
        <IconButton label={`Previous ${unit}`} icon="caret-left" tooltipShortcut="K" onClick={() => move(-1)}/>
        <Button className="ot-cal__today" onClick={today}>Today</Button>
        <IconButton label={`Next ${unit}`} icon="caret-right" tooltipShortcut="J" onClick={() => move(1)}/>
      </div>
      <dl className="ot-cal__sum">
        <div><dt>Tracked</dt><dd className="ot-mono">{formatDuration(tracked)}</dd></div>
        {currencies.map((currency) => <div key={currency}><dt>Earned, {currency}</dt><dd className="ot-mono">{formatMoney({ amount: earned[currency]!, currency })}</dd></div>)}
      </dl>
      <div className="ot-cal__tools">
        {view !== "month" && <div className="ot-cal__zoom" role="group" aria-label="Hour height">
          <IconButton className="is-ghost" label="Shorter hours" icon="zoom-out" tooltipShortcut="-" disabled={hour <= MIN_HOUR} onClick={() => setHour(hour - 8)}/>
          <IconButton className="is-ghost" label="Taller hours" icon="zoom-in" tooltipShortcut="+" disabled={hour >= MAX_HOUR} onClick={() => setHour(hour + 8)}/>
        </div>}
        <Segmented size="sm" aria-label="View" value={view} onChange={(next) => go(next, anchor)} options={viewOptions}/>
        <Menu label="Calendar options" items={[
          { label: settings.hideWeekends ? "Show weekends" : "Hide weekends", icon: settings.hideWeekends ? "eye" : "eye-off", onSelect: () => void app.act([factory.op("settings", "settings", { hideWeekends: !settings.hideWeekends })], settings.hideWeekends ? "Weekends shown" : "Weekends hidden", { undoable: false }) },
          { label: "Google Calendar", icon: "calendar", onSelect: () => { window.location.hash = "#/settings"; requestAnimationFrame(() => document.getElementById("settings-calendars")?.scrollIntoView({ behavior: "smooth" })); } },
          ...(google && googleState.phase === "connected" ? [{ label: "Refresh from Google", icon: "refresh" as const, onSelect: () => void google.sync({ force: true }).then(() => notify(google.store.getState().error ?? "Google Calendar refreshed")) }] : []),
          { label: "Keyboard shortcuts", icon: "command", hint: "?", onSelect: () => setHelp(true) }
        ]}/>
      </div>
    </header>

    {googleNotice === "reconnect" && <p className="ot-note ot-cal__notice"><Icon name="info"/><span>Google Calendar needs connecting again. Its events already on this device still show.</span><Button onClick={() => google?.connect()}>Reconnect</Button></p>}
    {googleNotice === "waiting" && <p className="ot-note ot-cal__notice"><Icon name="cloud"/><span>{googleState.pending.length === 1 ? "1 change waits" : `${googleState.pending.length} changes wait`} for Google Calendar. They go through once it can be reached.</span></p>}

    <motion.div className="ot-cal__frame" key={view} onPointerDownCapture={(event) => {
      const block = (event.target as HTMLElement).closest<HTMLElement>("[data-key]");
      lastPress.current = { x: event.clientX, y: event.clientY, key: block?.dataset["key"] ?? null, offset: block ? event.clientY - block.getBoundingClientRect().top : 0 };
    }} onKeyDownCapture={() => { lastPress.current = null; }} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}>
      {view === "month"
        ? <MonthGrid
          weeks={Array.from({ length: days.length / 7 }, (_, index) => days.slice(index * 7, index * 7 + 7))}
          month={anchor}
          entries={entries}
          plans={plans}
          dataset={dataset}
          now={now}
          selection={selection}
          onSelect={setSelection}
          onOpenItem={(key) => { const item = byKey.get(key); if (item && !isEntry(item)) editItem(item); }}
          onEditItem={editItem}
          onDay={(day) => go("day", day)}
          onDraft={openDraft}
          onMenu={openMenu}
          shiftItems={commands.shiftItems}
          notify={notify}
          draftKey={null}
        />
        : <TimeGrid
          days={days}
          entries={entries}
          plans={plans}
          dataset={dataset}
          now={now}
          hour={hour}
          onHour={setHour}
          selection={selection}
          onSelect={setSelection}
          draft={draft}
          onDraft={openDraft}
          onOpenItem={openItem}
          onEditItem={editItem}
          onBackground={onBackground}
          onMenu={openMenu}
          onDay={(day) => go("day", day)}
          hoverRef={hoverRef}
          commands={commands}
          compactWeekends={view === "week"}
          narrow={narrow}
          scrollKey={`${view}:${narrow}`}
          notify={notify}
        />}
    </motion.div>
    <p className="ot-cal__hint">{narrow ? "Tap empty time to add. Long-press for more." : <>Drag to add time or plans. <kbd>Shift</kbd> drag selects, <kbd>Alt</kbd> drag copies, right-click for more. Press <kbd>?</kbd> for shortcuts.</>}</p>

    {popover && (popover.mode === "draft" ? draft : popoverItem) && <CalendarPopover
      key={popover.mode === "item" ? popover.key : `draft:${draft?.kind}`}
      target={popover.mode === "draft" ? { mode: "draft", draft: draft! } : popover}
      item={popoverItem}
      dataset={dataset}
      commands={commands}
      anchor={anchorOf}
      pressedAt={() => (popover.mode === "item" && lastPress.current?.key === popover.key ? lastPress.current.offset : null)}
      onClose={() => { setPopover(null); setDraft(null); }}
      onDraftKind={(kind) => {
        if (!draft) return;
        const next = { ...draft, kind, end: kind === "entry" ? Math.min(draft.end, Date.now()) : draft.end };
        setDraft(next);
        setPopover({ mode: "draft", draft: next });
      }}
      onMore={(plan, more) => setEditor(plan ? { plan, draft: null } : { plan: null, draft: more })}
    />}
    {menu && <ContextMenu x={menu.x} y={menu.y} label={menu.target.item ? itemLabel(dataset, menu.target.item) : "Calendar"} items={menuItems(menu.target)} onClose={() => setMenu(null)}/>}
    <AnimatePresence>
      {editor && <EventEditor key="event-editor" plan={editor.plan} draft={editor.draft} commands={commands} onClose={() => setEditor(null)}/>}
      {scope && <ScopeDialog key="scope" verb={scope.verb} allowThis={scope.allowThis} onChoose={chooseScope}/>}
      {help && <Modal key="help" title="Calendar shortcuts" icon="command" onClose={() => setHelp(false)}>
        <dl className="ot-shortcuts">{SHORTCUTS.map(([keys, label]) => <div key={label}><dt>{keys.split("  ").map((group) => <kbd key={group}>{group}</kbd>)}</dt><dd>{label}</dd></div>)}</dl>
      </Modal>}
    </AnimatePresence>
  </div>;
}
